import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ColorSwatchIcon,
  PhotographIcon,
  PencilIcon,
  CheckCircleIcon,
  TrashIcon,
  UploadIcon,
  EyeIcon,
  EyeOffIcon,
} from "@heroicons/react/outline";
import {
  updateBrandingThunk,
  updateBrandColorsThunk,
  updateVisibilityThunk,
  uploadLogoThunk,
  deleteLogoThunk,
  uploadBannerThunk,
} from "../../../../store/thunks/branchDisplayThunks";

const BrandingSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { display, uploading, loadingSection } = useSelector(
    (state) => state.branchDisplay
  );

  const [isEditingBranding, setIsEditingBranding] = useState(false);
  const [isEditingColors, setIsEditingColors] = useState(false);
  const [isEditingVisibility, setIsEditingVisibility] = useState(false);

  const [brandingForm, setBrandingForm] = useState({
    displayName: "",
    tagline: "",
    shortDescription: "",
    fullDescription: "",
    storeStory: {
      title: "",
      content: "",
      founderName: "",
      foundingYear: "",
      generation: "",
    },
  });

  const [colorsForm, setColorsForm] = useState({
    primary: "#D4AF37",
    secondary: "#1a1a2e",
    accent: "#B8860B",
    background: "#FFFFFF",
    text: "#333333",
  });

  const [visibilityForm, setVisibilityForm] = useState({
    isPubliclyVisible: true,
    showOnWebsite: true,
    showOnApp: true,
    showLiveRates: false,
    showInventory: false,
  });

  useEffect(() => {
    if (display) {
      setBrandingForm({
        displayName: display.displayName || "",
        tagline: display.tagline || "",
        shortDescription: display.shortDescription || "",
        fullDescription: display.fullDescription || "",
        storeStory: {
          title: display.storeStory?.title || "",
          content: display.storeStory?.content || "",
          founderName: display.storeStory?.founderName || "",
          foundingYear: display.storeStory?.foundingYear || "",
          generation: display.storeStory?.generation || "",
        },
      });

      setColorsForm({
        primary: display.brandColors?.primary || "#D4AF37",
        secondary: display.brandColors?.secondary || "#1a1a2e",
        accent: display.brandColors?.accent || "#B8860B",
        background: display.brandColors?.background || "#FFFFFF",
        text: display.brandColors?.text || "#333333",
      });

      setVisibilityForm({
        isPubliclyVisible: display.isPubliclyVisible ?? true,
        showOnWebsite: display.showOnWebsite ?? true,
        showOnApp: display.showOnApp ?? true,
        showLiveRates: display.showLiveRates ?? false,
        showInventory: display.showInventory ?? false,
      });
    }
  }, [display]);

  const handleBrandingChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setBrandingForm((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setBrandingForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSaveBranding = async () => {
    await dispatch(
      updateBrandingThunk({
        branchCode,
        brandingData: brandingForm,
      })
    );
    setIsEditingBranding(false);
  };

  const handleSaveColors = async () => {
    await dispatch(
      updateBrandColorsThunk({
        branchCode,
        colors: colorsForm,
      })
    );
    setIsEditingColors(false);
  };

  const handleSaveVisibility = async () => {
    await dispatch(
      updateVisibilityThunk({
        branchCode,
        visibilitySettings: visibilityForm,
      })
    );
    setIsEditingVisibility(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("logo", file);

    await dispatch(uploadLogoThunk({ branchCode, formData }));
    e.target.value = "";
  };

  const handleDeleteLogo = async () => {
    if (window.confirm("Are you sure you want to delete the logo?")) {
      await dispatch(deleteLogoThunk(branchCode));
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("banner", file);

    await dispatch(uploadBannerThunk({ branchCode, formData }));
    e.target.value = "";
  };

  return (
    <div className="space-y-6">
      {/* Logo & Banner Section */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <PhotographIcon className="h-4 w-4 mr-2" />
          Logo & Banner
        </h4>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Logo */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">Store Logo</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center min-h-[150px] flex flex-col items-center justify-center">
              {display?.logo?.url ? (
                <div className="relative">
                  <img
                    src={display.logo.url}
                    alt="Store Logo"
                    className="w-24 h-24 object-contain rounded-lg"
                  />
                  {isRootAdmin && (
                    <button
                      onClick={handleDeleteLogo}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No logo uploaded</p>
              )}

              {isRootAdmin && (
                <label className="mt-3 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center gap-2 text-sm">
                  {uploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <UploadIcon className="h-4 w-4" />
                  )}
                  {uploading ? "Uploading..." : "Upload Logo"}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Banner */}
          <div className="lg:col-span-2">
            <label className="block text-sm text-gray-600 mb-2">Banner Image</label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center min-h-[150px] flex flex-col items-center justify-center bg-cover bg-center"
              style={{
                backgroundImage: display?.bannerImage?.url
                  ? `url(${display.bannerImage.url})`
                  : "none",
              }}
            >
              {!display?.bannerImage?.url && (
                <p className="text-sm text-gray-500">No banner uploaded</p>
              )}

              {isRootAdmin && (
                <label className="mt-3 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center gap-2 text-sm">
                  <UploadIcon className="h-4 w-4" />
                  Upload Banner
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleBannerUpload}
                  />
                </label>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Branding Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <PencilIcon className="h-4 w-4 mr-2" />
            Branding Information
          </h4>
          {isRootAdmin && !isEditingBranding && (
            <button
              onClick={() => setIsEditingBranding(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditingBranding ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Display Name</label>
                <input
                  type="text"
                  value={brandingForm.displayName}
                  onChange={(e) => handleBrandingChange("displayName", e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Store display name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Tagline</label>
                <input
                  type="text"
                  value={brandingForm.tagline}
                  onChange={(e) => handleBrandingChange("tagline", e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Store tagline"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Short Description</label>
              <textarea
                value={brandingForm.shortDescription}
                onChange={(e) => handleBrandingChange("shortDescription", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows="2"
                placeholder="Brief description for listings"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Full Description</label>
              <textarea
                value={brandingForm.fullDescription}
                onChange={(e) => handleBrandingChange("fullDescription", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows="4"
                placeholder="Detailed store description"
              />
            </div>

            {/* Store Story */}
            <div className="border-t pt-4 mt-4">
              <h5 className="font-medium mb-3">Store Story</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Story Title</label>
                  <input
                    type="text"
                    value={brandingForm.storeStory.title}
                    onChange={(e) => handleBrandingChange("storeStory.title", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Our Legacy"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Founder Name</label>
                  <input
                    type="text"
                    value={brandingForm.storeStory.founderName}
                    onChange={(e) => handleBrandingChange("storeStory.founderName", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Founder name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Founding Year</label>
                  <input
                    type="text"
                    value={brandingForm.storeStory.foundingYear}
                    onChange={(e) => handleBrandingChange("storeStory.foundingYear", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="1950"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Generation</label>
                  <input
                    type="text"
                    value={brandingForm.storeStory.generation}
                    onChange={(e) => handleBrandingChange("storeStory.generation", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="3rd Generation"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm text-gray-600 mb-1">Story Content</label>
                <textarea
                  value={brandingForm.storeStory.content}
                  onChange={(e) => handleBrandingChange("storeStory.content", e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="4"
                  placeholder="Tell your store's story..."
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                onClick={handleSaveBranding}
                disabled={loadingSection === "branding"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                {loadingSection === "branding" ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <CheckCircleIcon className="h-4 w-4" />
                )}
                Save Changes
              </button>
              <button
                onClick={() => setIsEditingBranding(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Display Name:</span>
                <p className="font-medium">{display?.displayName || "Not set"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Tagline:</span>
                <p className="font-medium">{display?.tagline || "Not set"}</p>
              </div>
            </div>
            {display?.shortDescription && (
              <div>
                <span className="text-sm text-gray-600">Short Description:</span>
                <p className="text-sm">{display.shortDescription}</p>
              </div>
            )}
            {display?.storeStory?.content && (
              <div className="border-t pt-3 mt-3">
                <span className="text-sm text-gray-600">Store Story:</span>
                <p className="text-sm mt-1">{display.storeStory.content}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Brand Colors */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <ColorSwatchIcon className="h-4 w-4 mr-2" />
            Brand Colors
          </h4>
          {isRootAdmin && !isEditingColors && (
            <button
              onClick={() => setIsEditingColors(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditingColors ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(colorsForm).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm text-gray-600 mb-1 capitalize">{key}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) =>
                        setColorsForm((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        setColorsForm((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      className="w-full px-2 py-1 border rounded text-sm font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-4">
              <button
                onClick={handleSaveColors}
                disabled={loadingSection === "brandColors"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save Colors
              </button>
              <button
                onClick={() => setIsEditingColors(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {Object.entries(display?.brandColors || colorsForm).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: value }}
                />
                <div>
                  <p className="text-xs text-gray-500 capitalize">{key}</p>
                  <p className="text-sm font-mono">{value}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visibility Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <EyeIcon className="h-4 w-4 mr-2" />
            Visibility Settings
          </h4>
          {isRootAdmin && !isEditingVisibility && (
            <button
              onClick={() => setIsEditingVisibility(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditingVisibility ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(visibilityForm).map(([key, value]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) =>
                      setVisibilityForm((prev) => ({ ...prev, [key]: e.target.checked }))
                    }
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm capitalize">
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex gap-2 pt-4">
              <button
                onClick={handleSaveVisibility}
                disabled={loadingSection === "visibility"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save Settings
              </button>
              <button
                onClick={() => setIsEditingVisibility(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {Object.entries(display || visibilityForm)
              .filter(([key]) => key.startsWith("is") || key.startsWith("show"))
              .map(([key, value]) => (
                <div
                  key={key}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                    value
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {value ? (
                    <EyeIcon className="h-4 w-4" />
                  ) : (
                    <EyeOffIcon className="h-4 w-4" />
                  )}
                  <span className="capitalize">
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandingSection;
